import { ResumeSGChargeSearchCriteria } from "domain/entity/ResumeSGCharge/ResumeSGChargeSearchCriteria";
import { ResumeSGChargeConstant } from "presentation/constant/ResumeSGCharge/ResumeSGChargeConstant";
import { useResumeSGChargeVM } from "presentation/hook/ResumeSGCharge/useResumeSGChargeVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useResumeSGChargeTracked } from "presentation/store/ResumeSGCharge/ResumeSGChargeProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const ResumeSGChargeSearchPanel = () => {
    const SCREEN_CONSTANT = ResumeSGChargeConstant;
    const [resumeSGChargeState] = useResumeSGChargeTracked();
    const resumeSGChargeVM = useResumeSGChargeVM();
    const { searchCriteria } = resumeSGChargeState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();

    const resetButtonClicked = useCallback(() => {
        resumeSGChargeVM.onRemoveAllSearchCriteria();
    }, [resumeSGChargeVM]);

    const searchButtonClicked = useCallback((searchCriteria: ResumeSGChargeSearchCriteria) => {

        if (searchCriteria.opsDateFrom &&
            searchCriteria.opsDateTo &&
            searchCriteria.opsDateFrom > searchCriteria.opsDateTo
        ) {
            messageBarVM.showError("Ops Date(To) should be later than or equals to Ops Date(From).");
            return;
        }

        if (!searchCriteria.voyageCode &&
            !searchCriteria.vesselCode &&
            !searchCriteria.opsDateFrom &&
            !searchCriteria.opsDateTo &&
            !searchCriteria.chargeType) {
            let errorCode1: string = `${SCREEN_CONSTANT.VOYAGE_CODE},${SCREEN_CONSTANT.VESSEL_CODE},
                                      ${SCREEN_CONSTANT.OPS_DATE_RANGE},
                                      ${SCREEN_CONSTANT.CHARGE_TYPE}`;
            let errorCode2: string = " must input one of them at least.";
            messageBarVM.showError(errorCode1 + errorCode2);
            return;
        }

        setIsLoading(true);
        resumeSGChargeVM.setIsSearchLoading(true);
        resumeSGChargeVM.onSearch(searchCriteria).then((data) => {
            setIsLoading(false);
            resumeSGChargeVM.setIsSearchLoading(false);
        }).catch(error => {
            setIsLoading(false);
            resumeSGChargeVM.setIsSearchLoading(false);
        })

    }, [SCREEN_CONSTANT.CHARGE_TYPE, SCREEN_CONSTANT.OPS_DATE_RANGE, SCREEN_CONSTANT.VESSEL_CODE, SCREEN_CONSTANT.VOYAGE_CODE, messageBarVM, resumeSGChargeVM])

    const memoSubChgTypeOptions = useMemo(() => {
        if (!searchCriteria.chargeType) {
            return Object.values(resumeSGChargeState.dynamicOptions.subChargeTypeDropdownOptions).flat();
        }
        const subChgTypeOptions = searchCriteria.chargeType ? resumeSGChargeState.dynamicOptions.subChargeTypeDropdownOptions[searchCriteria.chargeType] : [];
        return subChgTypeOptions;
    }, [searchCriteria.chargeType, resumeSGChargeState.dynamicOptions.subChargeTypeDropdownOptions])

    const memoConsortiumCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.consortiumCode ?? ''}
                fieldValue={searchCriteria?.consortiumCode}
                fieldLabel={SCREEN_CONSTANT.CONSORTIUM_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'consortiumCode'}
                maxLength={60}
                sort={true}
                options={resumeSGChargeState.dynamicOptions.consortiumCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    resumeSGChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.consortiumCode, SCREEN_CONSTANT.CONSORTIUM_CODE, resumeSGChargeState.dynamicOptions.consortiumCodeDropdownOptions, resumeSGChargeVM])

    const memoVesselCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.vesselCode ?? ''}
                fieldValue={searchCriteria?.vesselCode}
                fieldLabel={SCREEN_CONSTANT.VESSEL_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'vesselCode'}
                maxLength={6}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    resumeSGChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.VESSEL_CODE, resumeSGChargeVM, searchCriteria?.vesselCode])


    const memoVoyageCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.voyageCode ?? ''}
                fieldValue={searchCriteria?.voyageCode}
                fieldLabel={SCREEN_CONSTANT.VOYAGE_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'voyageCode'}
                maxLength={10}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    resumeSGChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.VOYAGE_CODE, resumeSGChargeVM, searchCriteria?.voyageCode])

    const memoChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.chargeType ?? ''}
                fieldValue={searchCriteria?.chargeType}
                fieldLabel={SCREEN_CONSTANT.CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeType'}
                maxLength={60}
                sort={false}
                options={resumeSGChargeState.dynamicOptions.chargeTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
                    resumeSGChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue);
                    resumeSGChargeVM.onSearchFieldChange('subChargeType', "", fFullValue);
                }} />
        </div>
        , [searchCriteria?.chargeType, SCREEN_CONSTANT.CHARGE_TYPE, resumeSGChargeState.dynamicOptions.chargeTypeDropdownOptions, resumeSGChargeVM])

    const memoSubChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.subChargeType ?? ''}
                fieldValue={searchCriteria?.subChargeType}
                fieldLabel={SCREEN_CONSTANT.SUB_CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subChargeType'}
                maxLength={60}
                sort={false}
                options={memoSubChgTypeOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    resumeSGChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.subChargeType, SCREEN_CONSTANT.SUB_CHARGE_TYPE, memoSubChgTypeOptions, resumeSGChargeVM])

    const memoOpsDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.OPS_DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "opsDateFrom", endField: "opsDateTo" }}
                dateRange={{ startDate: searchCriteria.opsDateFrom, endDate: searchCriteria.opsDateTo }}
                onDatesChange={resumeSGChargeVM.onDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.OPS_DATE_RANGE, resumeSGChargeVM.onDateRangeChange, searchCriteria.opsDateFrom, searchCriteria.opsDateTo])

    return (
        <div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px" }}>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{SCREEN_CONSTANT.SEARCH_CRITERIA}</SidebarTitle>
                        <SidebarActionCross>
                            <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={resumeSGChargeVM.onSearchClick} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>

            {isLoading && <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ maxHeight: '75vh', height: '75vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>

                        <CriteriaItemContainer>
                            {memoConsortiumCode}
                            {memoVesselCode}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoVoyageCode}
                            {memoChargeType}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoSubChargeType}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoOpsDateFromTo}
                        </CriteriaItemContainer>

                    </div>
                </div>
            </div>
            <SidebarActionBar>
                <HPHButton label={'Reset'} size={'Small'} theme={'Secondary'} onClick={resetButtonClicked} />
                <HPHButton label={'Search'} size={'Small'} theme={'Primary'} onClick={() => searchButtonClicked(searchCriteria)} />
            </SidebarActionBar>
        </div>
    );
}

export default ResumeSGChargeSearchPanel;
